import { nextTick, isRef } from 'vue';
import { createI18n } from 'vue-i18n';
import zh from '@/locales/zh.json';
import en from '@/locales/en.json';
import cloneDeep from 'lodash/cloneDeep';

import { LANG_EN, LANG_ZH, LANG_KEY } from '@/constant/lang';
import cookie from 'js-cookie';

import type { I18n, I18nOptions, Locale, VueI18n, Composer, I18nMode } from 'vue-i18n';

export const SUPPORT_LOCALES = [LANG_EN, LANG_ZH];

function isComposer(instance: VueI18n | Composer, mode: I18nMode): instance is Composer {
  return mode === 'composition' && isRef(instance.locale);
}

export function getLocale(i18n: I18n): string {
  if (isComposer(i18n.global, i18n.mode)) {
    return i18n.global.locale.value;
  } else {
    return i18n.global.locale;
  }
}

export function setLocale(i18n: I18n, locale: Locale): void {
  if (isComposer(i18n.global, i18n.mode)) {
    i18n.global.locale.value = locale;
  } else {
    i18n.global.locale = locale;
  }
}

export function setupI18n(options: I18nOptions = { locale: LANG_EN }) {
  // 设置html lang属性
  document.documentElement.lang = options.locale as string;
  const i18n = createI18n({ ...options });
  setI18nLanguage(i18n, options.locale!);
  return i18n;
}

export function setI18nLanguage(i18n: I18n, locale: Locale): void {
  setLocale(i18n, locale);
}

const getResourceMessages = (r: any) => r.default || r;

export async function asyncLoadMessages(i18n: I18n, locale: Locale) {
  const messages = await import(`./locales/${locale}.json`).then(getResourceMessages);
  i18n.global.setLocaleMessage(locale, messages);

  return nextTick();
}
const isValidLang = (lang: string) => {
  return SUPPORT_LOCALES.some((item) => item === lang);
};

export const getLocaleLang = () => {
  let localLang = localStorage.getItem(LANG_KEY);
  const allCookie = cookie.get();
  const cookieLang = allCookie[LANG_KEY];

  if (cookieLang && localLang !== cookieLang && isValidLang(cookieLang)) {
    localStorage.setItem(LANG_KEY, cookieLang);
    localLang = cookieLang;
  }
  // ai-copilot页面默认使用中文
  if (location.pathname.startsWith('/ai-copilot')) {
    localLang = LANG_ZH;
  }

  const navigatorLang = navigator.language.includes(LANG_ZH) ? LANG_ZH : LANG_EN;
  const lang = localLang || navigatorLang;

  return isValidLang(lang) ? lang : LANG_ZH;
};
const local = getLocaleLang();

/** 当前语言是否为中文 */
export const isZh = local === LANG_ZH;
/** 当前语言是否为英文 */
export const isEn = local === LANG_EN;

const _zh = zh; // reverseGitcodeToAtomgit(zh);
const _en = en; // reverseGitcodeToAtomgit(en);
const $i18n = setupI18n({
  legacy: false,
  locale: local,
  fallbackLocale: local,
  messages: {
    zh: {
      ..._zh
    },
    en: {
      ..._en
    }
  }
});

/** 项目 i18n 实例 */
export const i18n = $i18n;
